import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Block,
  ContentCopy,
  Edit,
  HelpOutline,
  History,
  Refresh,
  VpnKey,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '../contexts/PermissionContext';
import { useConfirmDialog } from './ConfirmDialog';
import {
  createPublicApiCredential,
  fetchPublicApiCredentialAudit,
  fetchPublicApiCredentials,
  revokePublicApiCredential,
  rotatePublicApiCredential,
  type PublicApiCredentialAuditEntry,
  type PublicApiCredentialPayload,
  type PublicApiCredentialSummary,
  type PublicApiScopeCatalogItem,
  updatePublicApiCredential,
} from '../services/publicApi';

interface PublicApiCredentialsManagerProps {
  mode: 'account' | 'tenant';
  tenantId?: string;
}

interface CredentialFormState {
  name: string;
  scopes: string[];
  allowedIpsInput: string;
  rateLimitPerMinute: string;
  expiresAt: string;
}

const DEFAULT_PUBLIC_API_RATE_LIMIT_PER_MINUTE = 120;

const PUBLIC_API_SCOPE_LABEL_KEYS: Record<string, string> = {
  'campaigns.read': 'pages:publicApi.scopeLabels.campaignsRead',
  'calls.read': 'pages:publicApi.scopeLabels.callsRead',
  'calls.write': 'pages:publicApi.scopeLabels.callsWrite',
  'calls.authorize_requested': 'pages:publicApi.scopeLabels.callsAuthorizeRequested',
  'agents.read': 'pages:publicApi.scopeLabels.agentsRead',
  'scripts.read': 'pages:publicApi.scopeLabels.scriptsRead',
  'users.read': 'pages:publicApi.scopeLabels.usersRead',
  'transfer_groups.read': 'pages:publicApi.scopeLabels.transferGroupsRead',
};

const initialFormState: CredentialFormState = {
  name: '',
  scopes: [],
  allowedIpsInput: '',
  rateLimitPerMinute: String(DEFAULT_PUBLIC_API_RATE_LIMIT_PER_MINUTE),
  expiresAt: '',
};

function parseAllowedIpsInput(value: string): string[] {
  const normalized: string[] = [];
  value
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .forEach((entry) => {
      if (!normalized.includes(entry)) {
        normalized.push(entry);
      }
    });
  return normalized;
}

function formatDateTimeInput(value?: string | null): string {
  if (!value) {
    return '';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
}

function formatDateTimeValue(value?: string | null): string {
  if (!value) {
    return '-';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '-';
}

export default function PublicApiCredentialsManager({ mode, tenantId }: PublicApiCredentialsManagerProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { hasPermission, isSuperuser } = usePermissions();
  const { confirm, alert, DialogComponent } = useConfirmDialog();

  const canRead = hasPermission('public_api_credentials_read');
  const canCreate = hasPermission('public_api_credentials_create');
  const canUpdate = hasPermission('public_api_credentials_update');
  const canRevoke = hasPermission('public_api_credentials_revoke');
  const canReadAudit = hasPermission('public_api_credentials_audit_read');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<PublicApiCredentialSummary[]>([]);
  const [availableScopes, setAvailableScopes] = useState<PublicApiScopeCatalogItem[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState<PublicApiCredentialSummary | null>(null);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<PublicApiCredentialAuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [formState, setFormState] = useState<CredentialFormState>(initialFormState);

  const managerContext = useMemo(() => {
    if (mode === 'account') {
      return { mode: 'account' } as const;
    }
    return tenantId ? ({ mode: 'tenant', tenantId } as const) : null;
  }, [mode, tenantId]);

  const loadCredentials = useCallback(async () => {
    if (!managerContext || !canRead) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchPublicApiCredentials(managerContext);
      setCredentials(response.credentials || []);
      setAvailableScopes(response.availableScopes || []);
      setError(null);
    } catch (loadError) {
      console.error('Error loading public API credentials:', loadError);
      setError(t('pages:publicApi.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [canRead, managerContext, t]);

  useEffect(() => {
    void loadCredentials();
  }, [loadCredentials]);

  const resetForm = () => {
    setEditingCredential(null);
    setFormState(initialFormState);
  };

  const openCreateDialog = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEditDialog = (credential: PublicApiCredentialSummary) => {
    setEditingCredential(credential);
    setFormState({
      name: credential.name,
      scopes: credential.scopes,
      allowedIpsInput: credential.allowedIps.join('\n'),
      rateLimitPerMinute: String(
        credential.rateLimitPerMinute || DEFAULT_PUBLIC_API_RATE_LIMIT_PER_MINUTE
      ),
      expiresAt: formatDateTimeInput(credential.expiresAt),
    });
    setFormOpen(true);
  };

  const handleToggleScope = (scopeCode: string) => {
    setFormState((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scopeCode)
        ? prev.scopes.filter((entry) => entry !== scopeCode)
        : [...prev.scopes, scopeCode],
    }));
  };

  const getScopeLabel = useCallback((scopeCode: string, fallbackLabel?: string) => {
    const translationKey = PUBLIC_API_SCOPE_LABEL_KEYS[scopeCode];
    if (!translationKey) {
      return fallbackLabel || scopeCode;
    }
    return t(translationKey, { defaultValue: fallbackLabel || scopeCode });
  }, [t]);

  const buildPayload = (): PublicApiCredentialPayload => {
    const payload: PublicApiCredentialPayload = {
      name: formState.name.trim(),
      scopes: formState.scopes,
      allowedIps: parseAllowedIpsInput(formState.allowedIpsInput),
      expiresAt: formState.expiresAt ? dayjs(formState.expiresAt).toISOString() : null,
    };
    if (isSuperuser) {
      payload.rateLimitPerMinute = Number(
        formState.rateLimitPerMinute || DEFAULT_PUBLIC_API_RATE_LIMIT_PER_MINUTE
      );
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!managerContext) {
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingCredential) {
        await updatePublicApiCredential(managerContext, editingCredential.id, buildPayload());
        setSuccess(t('pages:publicApi.feedback.updated'));
      } else {
        const response = await createPublicApiCredential(managerContext, buildPayload());
        setIssuedToken(response.bearerToken);
        setTokenDialogOpen(true);
        setSuccess(t('pages:publicApi.feedback.created'));
      }
      setFormOpen(false);
      resetForm();
      await loadCredentials();
    } catch (saveError) {
      console.error('Error saving public API credential:', saveError);
      setError(t('pages:publicApi.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToken = async () => {
    if (!issuedToken) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issuedToken);
      await alert({
        title: t('pages:publicApi.copyDialog.title'),
        message: t('pages:publicApi.copyDialog.success'),
        type: 'success',
      });
    } catch (copyError) {
      console.error('Error copying public API token:', copyError);
      setError(t('pages:publicApi.errors.copy'));
    }
  };

  const handleRotate = async (credential: PublicApiCredentialSummary) => {
    if (!managerContext) {
      return;
    }
    const confirmed = await confirm({
      title: t('pages:publicApi.rotateDialog.title'),
      message: t('pages:publicApi.rotateDialog.message', { name: credential.name }),
      confirmText: t('pages:publicApi.actions.rotate'),
      cancelText: t('common:actions.cancel'),
      type: 'warning',
      confirmIcon: <Refresh />,
    });
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await rotatePublicApiCredential(managerContext, credential.id);
      setIssuedToken(response.bearerToken);
      setTokenDialogOpen(true);
      setSuccess(t('pages:publicApi.feedback.rotated'));
      await loadCredentials();
    } catch (rotateError) {
      console.error('Error rotating public API credential:', rotateError);
      setError(t('pages:publicApi.errors.rotate'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (credential: PublicApiCredentialSummary) => {
    if (!managerContext) {
      return;
    }
    const confirmed = await confirm({
      title: t('pages:publicApi.revokeDialog.title'),
      message: t('pages:publicApi.revokeDialog.message', { name: credential.name }),
      confirmText: t('pages:publicApi.actions.revoke'),
      cancelText: t('common:actions.cancel'),
      type: 'warning',
      confirmIcon: <Block />,
    });
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await revokePublicApiCredential(managerContext, credential.id);
      setSuccess(t('pages:publicApi.feedback.revoked'));
      await loadCredentials();
    } catch (revokeError) {
      console.error('Error revoking public API credential:', revokeError);
      setError(t('pages:publicApi.errors.revoke'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAudit = async (credential: PublicApiCredentialSummary) => {
    if (!managerContext) {
      return;
    }
    setLoadingAudit(true);
    setAuditDialogOpen(true);
    try {
      const response = await fetchPublicApiCredentialAudit(managerContext, credential.id);
      setAuditEntries(response);
    } catch (auditError) {
      console.error('Error loading public API credential audit:', auditError);
      setAuditEntries([]);
      setError(t('pages:publicApi.errors.audit'));
    } finally {
      setLoadingAudit(false);
    }
  };

  if (mode === 'tenant' && !tenantId) {
    return <Alert severity="info">{t('pages:publicApi.saveTenantFirst')}</Alert>;
  }

  if (!canRead) {
    return <Alert severity="warning">{t('pages:publicApi.accessDenied')}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <VpnKey color="primary" />
                <Typography variant="h6">{t('pages:publicApi.title')}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t(
                  mode === 'account'
                    ? 'pages:publicApi.subtitleAccount'
                    : 'pages:publicApi.subtitleTenant'
                )}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                component={RouterLink}
                to="/help/public-api"
                variant="outlined"
                startIcon={<HelpOutline />}
              >
                {t('pages:publicApi.actions.help')}
              </Button>
              {canCreate && (
                <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
                  {t('pages:publicApi.actions.create')}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {loading ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      ) : credentials.length === 0 ? (
        <Alert severity="info">{t('pages:publicApi.empty')}</Alert>
      ) : (
        <Stack spacing={2}>
          {credentials.map((credential) => (
            <Card key={credential.id} variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6">{credential.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:publicApi.fields.keyId')}: <Box component="span" sx={{ fontFamily: 'monospace' }}>{credential.keyId}</Box>
                        {' · '}
                        {t('pages:publicApi.fields.secretSuffix')}: <Box component="span" sx={{ fontFamily: 'monospace' }}>{credential.secretLast4}</Box>
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Chip
                        color={credential.isActive ? 'success' : 'default'}
                        label={credential.isActive ? t('common:status.active') : t('common:status.inactive')}
                        size="small"
                      />
                      {canUpdate && credential.isActive && (
                        <Button size="small" startIcon={<Edit />} onClick={() => openEditDialog(credential)}>
                          {t('common:actions.edit')}
                        </Button>
                      )}
                      {canUpdate && credential.isActive && (
                        <Button size="small" startIcon={<Refresh />} onClick={() => void handleRotate(credential)}>
                          {t('pages:publicApi.actions.rotate')}
                        </Button>
                      )}
                      {canRevoke && credential.isActive && (
                        <Button size="small" color="warning" startIcon={<Block />} onClick={() => void handleRevoke(credential)}>
                          {t('pages:publicApi.actions.revoke')}
                        </Button>
                      )}
                      {canReadAudit && (
                        <Button size="small" startIcon={<History />} onClick={() => void handleOpenAudit(credential)}>
                          {t('pages:publicApi.actions.audit')}
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Divider />

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:publicApi.fields.scopes')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {credential.scopes.length > 0 ? (
                          credential.scopes.map((scope) => (
                            <Chip key={scope} label={getScopeLabel(scope)} size="small" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('pages:publicApi.noScopes')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:publicApi.fields.allowedIps')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {credential.allowedIps.length > 0
                          ? credential.allowedIps.join(', ')
                          : t('pages:publicApi.noAllowlist')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:publicApi.fields.rateLimit')}: {credential.rateLimitPerMinute}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:publicApi.fields.expiresAt')}: {formatDateTimeValue(credential.expiresAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:publicApi.fields.lastUsedAt')}: {formatDateTimeValue(credential.lastUsedAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:publicApi.fields.lastUsedIp')}: {credential.lastUsedIp || '-'}
                    </Typography>
                  </Stack>

                  {credential.revokedAt && (
                    <Alert severity="warning">
                      {t('pages:publicApi.revokedSummary', {
                        revokedAt: formatDateTimeValue(credential.revokedAt),
                        revokedBy: credential.revokedByName || '-',
                      })}
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCredential ? t('pages:publicApi.dialogs.editTitle') : t('pages:publicApi.dialogs.createTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              label={t('pages:publicApi.fields.name')}
              value={formState.name}
              onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
              required
              fullWidth
            />
            {isSuperuser ? (
              <TextField
                label={t('pages:publicApi.fields.rateLimit')}
                type="number"
                value={formState.rateLimitPerMinute}
                onChange={(event) => setFormState((prev) => ({ ...prev, rateLimitPerMinute: event.target.value }))}
                helperText={t('pages:publicApi.fields.rateLimitHelp')}
                fullWidth
              />
            ) : (
              <Alert severity="info">{t('pages:publicApi.fields.rateLimitRestrictedHelp')}</Alert>
            )}
            <TextField
              label={t('pages:publicApi.fields.expiresAt')}
              type="datetime-local"
              value={formState.expiresAt}
              onChange={(event) => setFormState((prev) => ({ ...prev, expiresAt: event.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={t('pages:publicApi.fields.expiresAtHelp')}
            />
            <TextField
              label={t('pages:publicApi.fields.allowedIps')}
              value={formState.allowedIpsInput}
              onChange={(event) => setFormState((prev) => ({ ...prev, allowedIpsInput: event.target.value }))}
              fullWidth
              multiline
              minRows={3}
              helperText={t('pages:publicApi.fields.allowedIpsHelp')}
            />
            {formState.scopes.includes('calls.write') && (
              <Alert severity="warning">
                {t('pages:publicApi.fields.callsWriteHelp')}
              </Alert>
            )}
            {formState.scopes.includes('calls.authorize_requested') && (
              <Alert severity="warning">
                {t('pages:publicApi.fields.callsAuthorizeRequestedHelp')}
              </Alert>
            )}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2">{t('pages:publicApi.fields.scopes')}</Typography>
                <Tooltip title={t('pages:publicApi.fields.scopesHelp')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <FormGroup>
                {availableScopes.map((scope) => (
                  <FormControlLabel
                    key={scope.code}
                    control={
                      <Checkbox
                        checked={formState.scopes.includes(scope.code)}
                        onChange={() => handleToggleScope(scope.code)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {getScopeLabel(scope.code, scope.label)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{scope.code}</Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button onClick={() => void handleSubmit()} variant="contained" disabled={saving || !formState.name.trim()}>
            {saving ? t('common:saving') : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tokenDialogOpen} onClose={() => setTokenDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('pages:publicApi.tokenDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">{t('pages:publicApi.tokenDialog.warning')}</Alert>
            <TextField
              label={t('pages:publicApi.tokenDialog.label')}
              value={issuedToken || ''}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopy />} onClick={() => void handleCopyToken()}>
            {t('pages:publicApi.actions.copyToken')}
          </Button>
          <Button variant="contained" onClick={() => setTokenDialogOpen(false)}>
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('pages:publicApi.auditDialog.title')}</DialogTitle>
        <DialogContent>
          {loadingAudit ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : auditEntries.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('pages:publicApi.auditDialog.empty')}
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {auditEntries.map((entry) => (
                <Card key={entry.id} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">{entry.action}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:publicApi.auditDialog.actor')}: {entry.actorUserName || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:publicApi.auditDialog.occurredAt')}: {formatDateTimeValue(entry.createdAt)}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        overflowX: 'auto',
                        fontSize: 12,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(entry.payload || {}, null, 2)}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)}>{t('common:actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {DialogComponent}
    </Stack>
  );
}
